import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { appointmentsAPI, reviewsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ReviewForm from '@/components/ReviewForm';
import {
  Calendar,
  Clock,
  Video,
  MapPin,
  Home,
  Stethoscope,
  ChevronRight,
  Loader2,
  Star,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';

const Appointments = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [pendingReviewIds, setPendingReviewIds] = useState(new Set());

  useEffect(() => {
    loadAppointments();
    loadPendingReviews();
  }, []);

  const loadAppointments = async () => {
    try {
      const response = await appointmentsAPI.getAll();
      setAppointments(response.data.appointments);
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingReviews = async () => {
    try {
      const response = await reviewsAPI.getMyPending();
      const pendingIds = new Set(response.data.pending_reviews.map(a => a.id));
      setPendingReviewIds(pendingIds);
    } catch (error) {
      console.error('Error loading pending reviews:', error);
    }
  };

  const handleJoinVideoCall = (appointmentId) => {
    navigate(`/video-room/${appointmentId}`);
  };

  const handleOpenReviewModal = (appointment, e) => {
    e.stopPropagation();
    setSelectedAppointment(appointment);
    setReviewModalOpen(true);
  };

  const handleReviewSubmitted = () => {
    // Remove from pending reviews
    setPendingReviewIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(selectedAppointment.id);
      return newSet;
    });
    setReviewModalOpen(false);
    setSelectedAppointment(null);
    loadAppointments();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800';
      case 'confirmed': return 'bg-red-100 text-red-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-emerald-100 text-emerald-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'confirmed': return 'Confirmé';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminé';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video_call': return Video;
      case 'home_visit': return Home;
      default: return MapPin;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'video_call': return 'Téléconsultation';
      case 'home_visit': return 'Domicile';
      default: return 'Sur place';
    }
  };

  const upcomingAppointments = appointments.filter(a => 
    ['pending', 'confirmed', 'in_progress'].includes(a.status)
  );
  const pastAppointments = appointments.filter(a => 
    ['completed', 'cancelled'].includes(a.status)
  );

  const renderAppointmentCard = (appointment) => {
    const TypeIcon = getTypeIcon(appointment.appointment_type);
    const canJoinVideo = appointment.appointment_type === 'video_call' && 
      ['confirmed', 'in_progress'].includes(appointment.status);
    const canReview = appointment.status === 'completed' && pendingReviewIds.has(appointment.id);
    
    return (
      <Card
        key={appointment.id}
        className="border-0 shadow-sm hover:shadow-md transition-all"
        data-testid={`appointment-card-${appointment.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
              <Stethoscope className="w-6 h-6 text-red-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-slate-900">
                    Dr. {appointment.professional_name}
                  </h3>
                  <p className="text-sm text-red-500">{appointment.professional_specialty}</p>
                </div>
                <Badge className={getStatusColor(appointment.status)}>
                  {getStatusLabel(appointment.status)}
                </Badge>
              </div>
              
              <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {appointment.date}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {appointment.time_slot}
                </span>
                <span className="flex items-center gap-1">
                  <TypeIcon className="w-4 h-4" />
                  {getTypeLabel(appointment.appointment_type)}
                </span>
              </div>

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-900">
                    {appointment.amount?.toLocaleString()} FCFA
                  </span>
                  <Badge className={appointment.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                    {appointment.payment_status === 'paid' ? 'Payé' : 'Non payé'}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Video Call Button */}
                  {canJoinVideo && (
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleJoinVideoCall(appointment.id);
                      }}
                      className="bg-[#1A237E] hover:bg-[#3949AB] text-white rounded-lg gap-1"
                      data-testid={`join-video-btn-${appointment.id}`}
                    >
                      <Video className="w-4 h-4" />
                      <span className="hidden sm:inline">Rejoindre</span>
                    </Button>
                  )}
                  
                  {/* Review Button */}
                  {canReview && (
                    <Button
                      size="sm"
                      onClick={(e) => handleOpenReviewModal(appointment, e)}
                      className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg gap-1"
                      data-testid={`review-btn-${appointment.id}`}
                    >
                      <Star className="w-4 h-4" />
                      <span className="hidden sm:inline">Donner un avis</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="section-padding py-6" data-testid="appointments-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Mes rendez-vous
        </h1>
        <Button
          onClick={() => navigate('/search')}
          className="bg-red-500 hover:bg-red-600 text-white rounded-full"
        >
          Nouveau RDV
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">
            À venir ({upcomingAppointments.length})
          </TabsTrigger>
          <TabsTrigger value="past" data-testid="tab-past">
            Passés ({pastAppointments.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming">
          {upcomingAppointments.length > 0 ? (
            <div className="space-y-4">
              {upcomingAppointments.map(renderAppointmentCard)}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700">Aucun rendez-vous à venir</h3>
              <p className="text-slate-500 mt-2">Prenez rendez-vous avec un professionnel</p>
              <Button
                onClick={() => navigate('/search')}
                className="mt-4 bg-red-500 hover:bg-red-600 rounded-full"
              >
                Trouver un professionnel
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="past">
          {pastAppointments.length > 0 ? (
            <div className="space-y-4">
              {pastAppointments.map(renderAppointmentCard)}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700">Aucun historique</h3>
              <p className="text-slate-500 mt-2">Vos consultations passées apparaîtront ici</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Review Modal */}
      <ReviewForm
        isOpen={reviewModalOpen}
        onClose={() => {
          setReviewModalOpen(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onReviewSubmitted={handleReviewSubmitted}
      />
    </div>
  );
};

export default Appointments;
